import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { escapeRegex } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { removePrefix, searchRedis } from '../server/embedding-search/redis-api.js';
import { Case } from './Case.js';
import { getAccountNumberFromDossierNumber, Machine } from './Machine.js';

@Entity()
export class Contact extends MongoEntity {
  static override _class = 'Contact';
  static override _collectionName = 'contacts';

  // System fields
  @Field(() => String) contactID: Maybe<string>;
  @Field(() => String) rowChecksum: Maybe<string>;
  @Field(() => Date) modifiedOn: Maybe<Date>;

  // Name fields
  @Field(() => String) fullName: Maybe<string>;
  @Field(() => String) firstName: Maybe<string>;
  @Field(() => String) middleName: Maybe<string>;
  @Field(() => String) lastName: Maybe<string>;

  // Company and department
  @Field(() => String) companyName: Maybe<string>;
  @Field(() => String) department: Maybe<string>;
  @Field(() => String) academicTitle: Maybe<string>;
  @Field(() => String) functionTitle: Maybe<string>;

  // Contact info
  @Field(() => String) businessPhone: Maybe<string>;
  @Field(() => String) extension: Maybe<string>;
  @Field(() => String) mobilePhone: Maybe<string>;
  @Field(() => String) email: Maybe<string>;

  // Address
  @Field(() => String) address1Street1: Maybe<string>;
  @Field(() => String) address1ZipPostalCode: Maybe<string>;
  @Field(() => String) address1City: Maybe<string>;
  @Field(() => String) address1CountryRegion: Maybe<string>;

  // Account link
  @Field(() => String) accountNumber: Maybe<string>;
  @Field(() => String) accountName: Maybe<string>;

  // Status
  @Field(() => String) status: Maybe<string>;
}

const phoneNumberAsRegexString = (phoneNumber: string) => {
  const number = phoneNumber.replace(/^(\+\d\d|0)/, '').replace(/\D/g, '');
  const numberRegexString = `^(\\+\\d\\d|0)${Array.from(number)
    .map(digit => `(\\D*${digit})`)
    .join('')}`;

  return {
    $or: [
      {
        mobilePhone: {
          $regex: numberRegexString,
        },
      },
      {
        businessPhone: {
          $regex: numberRegexString,
        },
      },
    ],
  };
};

const machinesWithSameAccountNumber = {
  from: Machine._collectionName,
  localField: 'accountNumber',
  foreignField: 'commercialAccountNumber',
  as: 'machines',
  pipeline: [{ $project: { _id: 1, dossierNumber: 1 } }],
};

const casesWithSameAccountName = {
  from: Case._collectionName,
  localField: 'accountName',
  foreignField: 'accountName',
  as: 'cases',
  pipeline: [{ $project: { _id: 1, caseNumber: 1, caseTitle: 1 } }],
};

export const getContactforPhoneNumber = async (
  phoneNumber: string,
): Promise<
  | (EntityData<Contact> & {
      machines: { _id: string; dossierNumber: string }[];
      cases: { _id: string; caseNumber: string; caseTitle: string }[];
    })
  | null
> => {
  if (!phoneNumber) {
    return null;
  }
  logger.info({ phoneNumber }, 'verify_phone tool called');
  const collection = await getRawCollection<Contact>(Contact._collectionName);
  // const contact = await collection.findOne();
  const contact = await collection
    .aggregate<
      Contact & {
        machines: { _id: string; dossierNumber: string }[];
        cases: { _id: string; caseNumber: string; caseTitle: string }[];
      }
    >([
      { $match: phoneNumberAsRegexString(phoneNumber) },
      { $limit: 1 },
      { $lookup: machinesWithSameAccountNumber },
      { $lookup: casesWithSameAccountName },
    ])
    .toArray()
    .then(vs => vs[0] ?? null);
  logger.info({ contact }, 'contact by phone number');
  if (contact?.mobilePhone || contact?.businessPhone) {
    return contact;
  } else {
    return null;
  }
};

export const getContactForName = async (
  name: string,
  dossierNumber?: string,
): Promise<
  | (EntityData<Contact> & {
      machines: { _id: string; dossierNumber: string }[];
      cases: { _id: string; caseNumber: string; caseTitle: string }[];
    })
  | null
> => {
  const accountNumber = dossierNumber ? await getAccountNumberFromDossierNumber(dossierNumber) : null;
  logger.info({ theName: name, dossierNumber, accountNumber }, 'getContactForName');
  const cleanName = name.trim();
  if (!cleanName) {
    return null;
  }

  const redisEntry = await searchRedis(name, accountNumber).then(vs => vs[0] ?? null);
  if (!redisEntry) {
    return basicNameSearch(name);
  }

  const contactID = removePrefix(redisEntry.id);
  const contact = getRawCollection(Contact._collectionName)
    .then(col =>
      col
        .aggregate<
          EntityData<Contact> & {
            machines: { _id: string; dossierNumber: string }[];
            cases: { _id: string; caseNumber: string; caseTitle: string }[];
          }
        >([
          { $match: { _id: contactID } },
          { $lookup: machinesWithSameAccountNumber },
          { $lookup: casesWithSameAccountName },
        ])
        .toArray(),
    )
    .then(vs => vs[0] ?? null);
  return contact;
};

async function basicNameSearch(cleanName: string) {
  const collection = await getRawCollection(Contact._collectionName);
  const result = await collection
    .aggregate<
      EntityData<Contact> & {
        machines: { _id: string; dossierNumber: string }[];
        cases: { _id: string; caseNumber: string; caseTitle: string }[];
      }
    >([
      {
        $addFields: {
          combinedName: { $concat: ['$firstName', ' ', '$lastName'] },
          reverseCombinedName: { $concat: ['$lastName', ' ', '$firstName'] },
        },
      },
      {
        $match: {
          $or: [
            { combinedName: { $regex: escapeRegex(cleanName), $options: 'i' } },
            { reverseCombinedName: { $regex: escapeRegex(cleanName), $options: 'i' } },
          ],
        },
      },
      { $limit: 1 },
      { $lookup: machinesWithSameAccountNumber },
      { $lookup: casesWithSameAccountName },
    ])
    .toArray()
    .then(vs => vs[0] ?? null)
    .catch(err => {
      logger.error({ err }, 'Error retrieving contact for name');
      return null;
    });

  return result;
}
